import { quotationRows, profileBom, glassBom, infillBom, hardwareOrder, barsByProfile } from "@/lib/bom";

export const DataTable = ({ cols, rows, testid, foot }) => (
  <div className="rounded-lg border border-slate-800 overflow-x-auto">
    <table className="w-full text-xs" data-testid={testid}>
      <thead className="bg-[#0d1220]">
        <tr>{cols.map((c) => <th key={c} className="text-left font-mono text-[10px] uppercase tracking-wider text-slate-400 py-2 px-2.5 border-b border-slate-800 whitespace-nowrap">{c}</th>)}</tr>
      </thead>
      <tbody>
        {rows.map((r, i) => (
          <tr key={i} className="border-b border-slate-800/70 hover:bg-slate-800/40 transition-colors" data-testid={`${testid}-row`}>
            {r.map((v, j) => <td key={j} className={`py-1.5 px-2.5 ${typeof v === "number" ? "font-mono text-cyan-200" : "text-slate-200"}`}>{v}</td>)}
          </tr>
        ))}
      </tbody>
      {foot && <tfoot><tr className="bg-[#0d1220]">{foot.map((v, j) => <td key={j} className="py-2 px-2.5 font-mono text-xs font-semibold text-slate-100">{v}</td>)}</tr></tfoot>}
    </table>
  </div>
);

const H = ({ children }) => <h3 className="font-heading text-sm font-semibold text-slate-100 mb-2 mt-1">{children}</h3>;

export const QuotationTab = ({ items, s }) => {
  const q = quotationRows(items);
  const qty = q.reduce((a, r) => a + r.qty, 0), area = q.reduce((a, r) => a + r.total, 0);
  return (
    <div className="space-y-3">
      <DataTable testid="bom-quotation-table" cols={["Sr", "Tag", "Description", "Typology", "System", "Location", "W × H mm", "m²/unit", "Qty", "Total m²"]}
        rows={q.map((r) => [r.sr, r.tag, r.desc, r.typology, r.system, r.location || "—", `${r.w} × ${r.h}`, r.area, r.qty, r.total])}
        foot={["", "", "Total", "", "", "", "", "", qty, Math.round(area * 100) / 100]} />
      <p className="text-xs text-slate-400" data-testid="bom-quotation-note">
        Quantities only. The Excel quotation has blank rate cells for each item, and works out amounts, GST at <span className="text-cyan-300 font-mono">{s.gst}%</span> and the grand total automatically.
      </p>
    </div>
  );
};

const Swatch = ({ m }) => <span className="inline-flex items-center gap-1.5 whitespace-nowrap"><span className="w-3 h-3 rounded-sm border border-slate-500 shrink-0" style={{ background: m.print }} />{m.label}</span>;

const InfillSection = ({ items }) => {
  const { rows, totals } = infillBom(items);
  if (!rows.length) return null;
  return (
    <div data-testid="bom-infill-section">
      <H>Infill panels — non-glass (sizes incl. bite)</H>
      <DataTable testid="bom-infill-table" cols={["Material", "Specification", "Panels", "Area m²", "Used in"]} rows={rows.map((r) => [<Swatch m={r.mat} />, r.spec, r.panes, r.area, r.tags])} />
      <div className="mt-2 flex flex-wrap gap-2" data-testid="bom-infill-totals">
        {totals.map((t) => (
          <span key={t.mat.key} className="rounded-md border border-slate-700 bg-[#0d1220] px-2.5 py-1 text-xs flex items-center gap-2" data-testid={`bom-infill-total-${t.mat.key}`}>
            <Swatch m={t.mat} /><span className="font-mono text-cyan-200">{t.area} m²</span><span className="text-slate-500">· {t.panes} panel{t.panes === 1 ? "" : "s"}</span>
          </span>
        ))}
      </div>
    </div>
  );
};

export const MaterialsTab = ({ items, plan }) => {
  const bars = barsByProfile(plan);
  return (
    <div className="space-y-5">
      <div>
        <H>Profiles</H>
        <DataTable testid="bom-profile-table" cols={["Profile / member", "Code", "System", "Pieces", "Length m", "Bars", "Used in"]}
          rows={profileBom(items).map((p) => [p.name, p.code || "—", p.system, p.count, p.m, bars.get(p.key) || 0, p.tags])} />
      </div>
      <div>
        <H>Glass (sizes incl. bite)</H>
        <DataTable testid="bom-glass-table" cols={["Specification", "Panes", "Area m²", "Used in"]} rows={glassBom(items).map((g) => [g.spec, g.panes, g.area, g.tags])} />
      </div>
      <InfillSection items={items} />
      <p className="text-xs text-slate-400">Profile weights: enter kg/m from the IWIN-S / YKK AP catalogue in the Excel BOM tab and the weights work out automatically.</p>
    </div>
  );
};

export const HardwareTab = ({ items }) => (
  <div className="space-y-5">
    {hardwareOrder(items).map((g, i) => (
      <div key={g.group} data-testid={`hardware-group-${i}`}>
        <H><span className={g.group.startsWith("IWIN") ? "text-amber-300" : "text-slate-300"}>{g.group}</span><span className="ml-2 font-mono text-[11px] text-slate-500">{g.rows.length} lines</span></H>
        <DataTable testid={`hardware-order-table-${i}`} cols={["Part", "Specification", "Total qty", "Unit", "Items (tag × qty)"]} rows={g.rows.map((h) => [h.name, h.spec || "—", h.qty, h.unit, h.tags])} />
      </div>
    ))}
  </div>
);
